import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Platform } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useRoute } from '@react-navigation/native';
import { isFirstDegreeOrHigher } from '../utils/gradeAccess';
import { canAccessBuilderMain, BUILDER_SCREEN_NAMES } from '../utils/builderAccess';
import { clearLoggedInSession, hasSuperUserAccess } from '../utils/sessionUser';
import InviteWebsiteQrModal from './InviteWebsiteQrModal';
import DebugConsoleModal from './DebugConsoleModal';

function resolveMenuUser(route, navigation, currentUserProp) {
  if (currentUserProp) return currentUserProp;
  if (route?.params?.user) return route.params.user;
  const navState = navigation?.getState?.();
  const mainPageRoute = navState?.routes?.find((r) => r.name === 'MainPage');
  if (mainPageRoute?.params?.user) return mainPageRoute.params.user;
  return null;
}

export default function HamburgerMenu({ navigation, onProfilePress, stopwatchRef, currentUser }) {
  const theme = useTheme();
  const route = useRoute();
  const [menuOpen, setMenuOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const currentRouteName = route?.name || '';
  const isMainPage = currentRouteName === 'MainPage';
  const isMyCoursesFlow =
    currentRouteName === 'MyCourses' ||
    currentRouteName === 'MyCourseDetail' ||
    currentRouteName === 'ChiGungHandbook' ||
    currentRouteName === 'ChiGungHandbookForm' ||
    currentRouteName === 'ChiGungHandbookFormComment' ||
    currentRouteName === 'DimMakHandbook' ||
    String(currentRouteName).startsWith('dim_mak_');

  // Check if we're on a builder page
  const builderPages = BUILDER_SCREEN_NAMES.filter((name) => name !== 'BuilderMain');
  const isBuilderPage = builderPages.includes(currentRouteName);

  const handleThemeToggle = () => {
    theme.toggleTheme();
  };

  const handleMainPagePress = () => {
    if (!isMainPage && navigation) {
      setMenuOpen(false);
      // Try to get user from current route params or navigation state
      const currentUser = route?.params?.user;
      const navState = navigation.getState();
      const mainPageRoute = navState?.routes?.find(r => r.name === 'MainPage');
      const mainPageUser = mainPageRoute?.params?.user;
      const user = currentUser || mainPageUser;
      navigation.navigate('MainPage', user ? { user } : {});
    }
  };

  const handleBackToBuilders = () => {
    if (isBuilderPage && navigation) {
      setMenuOpen(false);
      const menuUser = resolveMenuUser(route, navigation, currentUser);
      navigation.navigate('BuilderMain', {
        userId: menuUser?.id ?? route?.params?.userId ?? 1,
        user: menuUser ?? route?.params?.user,
      });
    }
  };

  const handleStopwatchPress = () => {
    setMenuOpen(false);
    if (stopwatchRef?.current) {
      stopwatchRef.current.showModal();
    }
  };

  const styles = getStyles(theme);
  const menuUser = resolveMenuUser(route, navigation, currentUser);
  const showClubStats = navigation && isFirstDegreeOrHigher(menuUser);
  const showDebug = hasSuperUserAccess(menuUser);

  useEffect(() => {
    if (Platform.OS !== 'web' || !menuOpen || typeof window === 'undefined') return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const menuModalContent = (
        <View
          style={styles.modalRoot}
          {...(Platform.OS === 'web' ? { nativeID: 'hamburger-menu-modal-root' } : {})}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setMenuOpen(false)}
            {...(Platform.OS === 'web' ? { nativeID: 'hamburger-menu-backdrop' } : {})}
          >
            <View
              style={styles.menu}
              onStartShouldSetResponder={() => true}
              {...(Platform.OS === 'web' ? { nativeID: 'hamburger-menu-panel' } : {})}
            >
              <View style={styles.menuBody}>
            {/* Back to Builders - First Entry (only on builder pages) */}
            {isBuilderPage && canAccessBuilderMain(menuUser) && (
              <TouchableOpacity
                style={styles.menuItem}
                onPress={handleBackToBuilders}
                activeOpacity={0.7}
              >
                <Text style={styles.menuItemText}>Back</Text>
              </TouchableOpacity>
            )}
            
            {/* Main Page — back home (emphasized on My Courses) */}
            <TouchableOpacity
              style={[
                styles.menuItem,
                isMainPage && styles.menuItemDisabled,
                isMyCoursesFlow && !isMainPage && styles.menuItemBackHome,
              ]}
              onPress={handleMainPagePress}
              activeOpacity={isMainPage ? 1 : 0.7}
              disabled={isMainPage}
            >
              <Text
                style={[
                  styles.menuItemText,
                  isMainPage && styles.menuItemTextDisabled,
                  isMyCoursesFlow && !isMainPage && styles.menuItemBackHomeText,
                ]}
              >
                Main Page
              </Text>
            </TouchableOpacity>

            {showClubStats && (
              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => {
                  setMenuOpen(false);
                  const u = resolveMenuUser(route, navigation, currentUser);
                  navigation.navigate('ClubStats', { user: u, userId: u?.id });
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.menuItemText}>Club Stats</Text>
              </TouchableOpacity>
            )}

            {onProfilePress && (
              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => {
                  setMenuOpen(false);
                  onProfilePress();
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.menuItemText}>Profile</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={styles.menuItem}
              onPress={handleStopwatchPress}
              activeOpacity={0.7}
            >
              <View style={styles.menuItemWithIcon}>
                <Text style={styles.menuItemText}>Stopwatch</Text>
                <Text style={styles.menuItemIcon}>⏱️</Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.menuItem}
              onPress={() => {
                handleThemeToggle();
                setMenuOpen(false);
              }}
              activeOpacity={0.7}
            >
              <View style={styles.menuItemWithIcon}>
                <Text style={styles.menuItemText}>
                  {theme.isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </Text>
                <Text style={styles.menuItemIcon}>
                  {theme.isDarkMode ? '☀️' : '🌙'}
                </Text>
              </View>
            </TouchableOpacity>
            {isMainPage && (
              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => {
                  setMenuOpen(false);
                  setInviteModalOpen(true);
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.menuItemText}>Invite to Website</Text>
              </TouchableOpacity>
            )}
            {showDebug && (
              <TouchableOpacity
                style={styles.menuItem}
                onPress={() => {
                  setMenuOpen(false);
                  setDebugModalOpen(true);
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.menuItemText}>Debug</Text>
              </TouchableOpacity>
            )}
            {navigation && (
              <TouchableOpacity
                style={[styles.menuItem, styles.menuItemLast]}
                onPress={async () => {
                  setMenuOpen(false);
                  await clearLoggedInSession();
                  navigation.replace('Login');
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.menuItemText}>Logout</Text>
              </TouchableOpacity>
            )}
              </View>
          </View>
        </TouchableOpacity>
        </View>
  );

  const webOverlay =
    Platform.OS === 'web' &&
    menuOpen &&
    typeof document !== 'undefined' &&
    require('react-dom').createPortal(
      <div
        id="hamburger-menu-web-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          pointerEvents: 'auto',
        }}
      >
        {menuModalContent}
      </div>,
      document.body
    );

  return (
    <>
      <TouchableOpacity
        style={styles.hamburger}
        onPress={() => setMenuOpen(!menuOpen)}
        activeOpacity={0.7}
        nativeID="header-hamburger-trigger"
        dataSet={{
          hamburgerButton: 'true',
        }}
      >
        <View
          style={styles.hamburgerContainer}
          dataSet={{
            hamburgerInner: 'true',
          }}
        >
          <View
            style={[styles.hamburgerLine, menuOpen && styles.hamburgerLineActive]}
            dataSet={{
              hamburgerLine: 'true',
              ...(menuOpen ? { hamburgerLineActive: 'true' } : {}),
            }}
          />
          <View
            style={[styles.hamburgerLine, menuOpen && styles.hamburgerLineActive]}
            dataSet={{
              hamburgerLine: 'true',
              ...(menuOpen ? { hamburgerLineActive: 'true' } : {}),
            }}
          />
          <View
            style={[styles.hamburgerLine, menuOpen && styles.hamburgerLineActive]}
            dataSet={{
              hamburgerLine: 'true',
              ...(menuOpen ? { hamburgerLineActive: 'true' } : {}),
            }}
          />
        </View>
      </TouchableOpacity>

      {Platform.OS === 'web'
        ? webOverlay
        : (
          <Modal
            visible={menuOpen}
            transparent={true}
            animationType="fade"
            onRequestClose={() => setMenuOpen(false)}
          >
            {menuModalContent}
          </Modal>
        )}
      <InviteWebsiteQrModal
        visible={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
      />
      <DebugConsoleModal visible={debugModalOpen} onClose={() => setDebugModalOpen(false)} />
    </>
  );
}

const getStyles = (theme) => StyleSheet.create({
  hamburger: {
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    /* Solid fill required on web: global.css forces opaque black on views without r-backgroundColor */
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  hamburgerContainer: {
    width: 24,
    height: 18,
    justifyContent: 'space-between',
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  hamburgerLine: {
    width: 24,
    height: 3,
    /* Matches page headings: gold in light (`#d4af37`), bright yellow in dark. */
    backgroundColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    borderRadius: 2,
  },
  hamburgerLineActive: {
    backgroundColor: theme.isDarkMode ? theme.colors.text : theme.colors.secondary,
  },
  modalRoot: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'transparent',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    paddingTop: Platform.OS === 'web' ? 100 : 100,
    paddingLeft: 20,
  },
  menu: {
    width: 200,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    ...(theme.isDarkMode
      ? {
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: theme.colors.border,
          borderRightWidth: 1,
          borderRightColor: theme.colors.border,
          borderTopWidth: 1,
          borderTopColor: theme.colors.border,
          borderLeftWidth: 1,
          borderLeftColor: theme.colors.border,
        }
      : {
          borderWidth: 2,
          borderColor: '#000000',
          paddingVertical: 0,
          overflow: 'hidden',
        }),
    ...(Platform.OS === 'web' && {
      boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.25)',
    }),
  },
  menuBody: {
    width: '100%',
    paddingVertical: theme.isDarkMode ? 0 : 10,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  menuItem: {
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  menuItemWithIcon: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  menuItemLast: {
    borderBottomWidth: 0,
  },
  menuItemText: {
    fontSize: 18,
    color: theme.colors.text,
  },
  menuItemTextDisabled: {
    color: theme.isDarkMode ? '#666' : '#999',
    opacity: 0.5,
  },
  menuItemDisabled: {
    opacity: 0.5,
  },
  menuItemBackHome: {
    backgroundColor: theme.isDarkMode ? 'rgba(212, 175, 55, 0.12)' : 'rgba(212, 175, 55, 0.15)',
    borderLeftWidth: 3,
    borderLeftColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
  },
  menuItemBackHomeText: {
    fontWeight: '700',
  },
  menuItemIcon: {
    fontSize: 20,
  },
});

